/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Alert, Stack } from "@mui/material";
import { FC, type JSX, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { PageContainer } from "../../../components/layout/PageContainer";
import TitleBox from "../../../components/TitleBox";
import { useThemeBackgroundColor } from "../../../hooks/useThemeBackgroundColor";
import TestIds from "../../../testIds";
import { selectCurrentFarmerAccount } from "../../azure-b2c/storage";
import { useActivities, useActivitiesLookups } from "../api/hooks/useActivities";
import { ActivitiesResponse, Activity, LookupsResponse } from "../api/types/types";
import ActivitiesHeader from "../components/ActivitiesHeader.tsx/ActivitiesHeader";
import ActivitiesList from "../components/ActivitiesList/ActivitiesList";
import ContactDetailDisplay from "../components/ContactDetailDisplay/ContactDetailDisplay";
import CreateActivityModal from "../components/CreateActivityModal/CreateActivityModal";
import { ACTIVITIES_ROUTES } from "../constants";

const Activities: FC = (): JSX.Element => {
  const account = useSelector(selectCurrentFarmerAccount);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const navigate = useNavigate();

  const {
    data: activitiesData,
    isLoading: activitiesLoading,
    error: activitiesError,
  } = useActivities(account?.accountNumber ?? "");
  const {
    data: lookupsData,
    isLoading: lookupsLoading,
    error: lookupsError,
  } = useActivitiesLookups();
  useThemeBackgroundColor((theme) => theme.colors.shade0);

  const campaigns = (activitiesData as ActivitiesResponse)?.campaigns || [];
  const lookups = (lookupsData as LookupsResponse)?.lookups || [];

  if (activitiesError || lookupsError) {
    return (
      <PageContainer data-testid={TestIds.pages.activities}>
        <TitleBox display="Activities" />
        <Alert severity="error" data-testid={TestIds.feat.activities.error}>
          Failed to load activities. Please try again later.
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer data-testid={TestIds.pages.activities}>
      <ActivitiesHeader onCreateActivity={() => setIsCreateModalOpen(true)} />

      <Stack
        gap={{ xs: 3, lg: 2 }}
        direction={{ xs: "column", lg: "row" }}
        alignItems={{ xs: "stretch", lg: "start" }}
      >
        <ActivitiesList
          loading={activitiesLoading || lookupsLoading}
          activities={(activitiesData as ActivitiesResponse)?.activities || []}
          lookups={lookups}
          onActivityClick={(activity: Activity) =>
            navigate(`${ACTIVITIES_ROUTES.BASE}/${activity.activityId}`)
          }
        />
        <ContactDetailDisplay />
      </Stack>

      {isCreateModalOpen && (
        <CreateActivityModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          contactPersons={(activitiesData as ActivitiesResponse)?.contactPersons || []}
          campaigns={campaigns}
          lookups={lookups}
        />
      )}
    </PageContainer>
  );
};

export default Activities;
